import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { fetchAuthenticatedBlob } from '../api/client';
import { useAuth } from '../features/auth/AuthProvider';

const MANAGED_PATIENT_AVATAR_PATH = '/patient/profile/avatar/content';

type PatientAvatarImageProps = {
  readonly patientId: string;
  readonly avatarUrl: string | null | undefined;
  readonly alt?: string;
  readonly className?: string;
  readonly fallback: ReactNode;
};

/**
 * Renders patient.avatar_url: absolute http(s) URLs directly; uploaded (managed) avatars
 * via authenticated admin content endpoint.
 */
export function PatientAvatarImage({
  patientId,
  avatarUrl,
  alt = '',
  className,
  fallback,
}: PatientAvatarImageProps) {
  const { tokens } = useAuth();
  const absoluteHttp = useMemo(() => {
    const t = avatarUrl?.trim() ?? '';
    if (!t) {
      return null;
    }
    return /^https?:\/\//i.test(t) ? t : null;
  }, [avatarUrl]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (absoluteHttp) {
      return;
    }
    const t = avatarUrl?.trim() ?? '';
    if (!t) {
      setBlobUrl(null);
      return;
    }
    const normalized = t.startsWith('/') ? t : `/${t}`;
    const isManaged = normalized === MANAGED_PATIENT_AVATAR_PATH;
    if (!isManaged) {
      setBlobUrl(null);
      return;
    }
    const token = tokens?.access_token;
    if (!token) {
      setBlobUrl(null);
      return;
    }
    const path = `/admin/patients/${patientId}/avatar/content`;
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchAuthenticatedBlob(path, token).then(
      (blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      },
      () => {
        if (!cancelled) {
          setBlobUrl(null);
        }
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [avatarUrl, patientId, tokens?.access_token, absoluteHttp]);

  const src = absoluteHttp ?? blobUrl;
  if (!src) {
    return <>{fallback}</>;
  }
  return <img src={src} alt={alt} className={className} />;
}
